import { useEffect, useState } from 'react';
import API from '../../api/api';
import { RenderStarted } from '../../api/clip';
import { User } from '../../api/structs/user';
import { useToast } from '../../hooks/useToast';
import {
  getClipRenderState,
  markClipRenderCancellable,
  markClipRenderStarting,
  restoreClipRenderState,
  useClipRenderState,
} from './renderState';
import {
  isTelegramSendActive,
  markTelegramSendStarted,
  telegramSendLabel,
  useTelegramSendState,
} from './telegramSendState';

interface UseClipActionsResult {
  isRendering: boolean;
  /** True while a BACKGROUND send is queued, compressing or uploading. */
  isSendingTelegram: boolean;
  /** What the send button should say right now, e.g. "Compressing…". */
  telegramSendLabelText: string;
  isSendingCarousel: boolean;
  isCancellingRender: boolean;
  handleDelete: () => Promise<void>;
  handleRender: () => Promise<void>;
  handleCancelRender: () => Promise<void>;
  handleSendToTelegram: () => Promise<void>;
  handleSendCarouselToTelegram: () => Promise<void>;
}

/**
 * M5: friendly text for the refusal codes `packageRender.go:366-389` sends
 * (a plan-hold 402 `insufficient_credits`/`daily_limit_exceeded`, or a 403
 * `feature_not_allowed`), instead of showing that raw code. Mirrors the
 * app's EXISTING wording for the same codes rather than inventing new copy:
 * the two credits codes word identically to App.tsx's `onCreditsError` toast
 * (the same 402 also reaches that global interceptor, so the two must not
 * say different things), and the feature text follows the app's established
 * "ask an admin to enable … for this account" phrasing (App.tsx's
 * `NoAccessView`). Returns null for anything else so the raw body text
 * underneath still shows through for an unmapped code.
 */
const knownRenderRefusalText = (data: Record<string, unknown>): string | null => {
  const need = typeof data.need === 'number' ? data.need : undefined;
  switch (data.error) {
    case 'insufficient_credits':
      return `Not enough credits${need ? ` (need ${need.toLocaleString()})` : ''} — ask admin to top up.`;
    case 'daily_limit_exceeded':
      return 'Daily credit limit reached — try again tomorrow or ask admin to raise your cap.';
    case 'feature_not_allowed':
      return "Rendering isn't enabled for this account — ask an admin to enable it.";
    default:
      return null;
  }
};

/**
 * What a failed render actually said.
 *
 * A refused render answers 409 with `{error, media_id, state}` — the media item
 * that is not ready and what state it is in. Reporting only `error.message`
 * turned that into "Request failed with status code 409", which names neither
 * the blocking item nor the reason, and the blocker is usually an audio take
 * the user can then go and remove.
 */
export const renderErrorText = (error: any): string => {
  const data = error?.response?.data;
  const fallback = error?.message || 'Render failed';
  if (!data || typeof data !== 'object') return fallback;

  // CT-5: show the amount only when the backend sent a positive estimate.
  if (data.error === 'insufficient_credits' && typeof data.estimate_credits === 'number' && data.estimate_credits > 0) {
    return `Not enough credits (this render needs about ${data.estimate_credits})`;
  }

  const known = knownRenderRefusalText(data);
  if (known) return known;

  const bodyError = typeof data.error === 'string' ? data.error.trim() : '';
  const parts = [bodyError || fallback];
  const mediaId = String(data.media_id ?? '').trim();
  const mediaState = String(data.state ?? '').trim();
  if (mediaId) parts.push(`media ${mediaId}`);
  if (mediaState) parts.push(mediaState);
  return parts.join(' — ');
};

/** The estimate of a `409 confirm_required` render answer, else null. */
export const confirmRequiredEstimate = (error: any): number | null => {
  const data = error?.response?.data;
  if (error?.response?.status !== 409 || data?.error !== 'confirm_required') return null;
  return typeof data.estimate_credits === 'number' ? data.estimate_credits : null;
};

/** The start toast: unchanged for built-ins, with the estimate for a generated style. */
export const renderStartedText = (started: RenderStarted | undefined): string =>
  typeof started?.estimate_credits === 'number' && started.estimate_credits > 0
    ? `Render started (≈ ${started.estimate_credits} credits)`
    : 'Render started';

/**
 * Delete / render / send-to-Telegram actions for a clip. Kept separate from
 * the draft-editor concern (useClipDraftEditor) so ClipPromptItem only wires
 * together state + handlers rather than owning both.
 */
export function useClipActions(
  clipId: string,
  fileUrls: string[],
  clipCaption: string,
  activeUser: User | null,
  onRefresh: () => void,
  waitForPendingSaves: () => Promise<void>,
  orderedImagePaths: string[]
): UseClipActionsResult {
  const toast = useToast();
  const [isRendering, setIsRendering] = useState(false);
  // Only the POST that CREATES the job — a blink. Everything after it is
  // reported by the store, which survives this component and the page.
  const [isStartingTelegram, setIsStartingTelegram] = useState(false);
  const telegramSend = useTelegramSendState(clipId);
  const [isSendingCarousel, setIsSendingCarousel] = useState(false);
  const [isCancellingRender, setIsCancellingRender] = useState(false);
  const liveRenderState = useClipRenderState(clipId);

  // Minor a: a successful cancel POST only ACCEPTED the request — the render
  // itself keeps going, asynchronously, until the backend's own terminal WS
  // event lands. Stay disabled/"Cancelling…" for that whole window, not just
  // the POST's own round trip.
  useEffect(() => {
    if (isCancellingRender && liveRenderState.status !== 'rendering') {
      setIsCancellingRender(false);
    }
  }, [isCancellingRender, liveRenderState.status]);

  const blurActiveElement = async () => {
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
      await new Promise((resolve) => window.setTimeout(resolve, 0));
    }
  };

  const handleDelete = async () => {
    try {
      await API.deleteClipPrompt(clipId);
      onRefresh();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleRender = async () => {
    setIsRendering(true);
    // I2: captured BEFORE the optimistic bridge below overwrites the store,
    // so any restore below can put back exactly what was showing before this
    // call — never a blank idle state.
    const priorRenderState = getClipRenderState(clipId);
    // Optimistic bridge: show the bar immediately; live RenderProgress WS
    // events refine it until completion.
    markClipRenderStarting(clipId);
    // Fix round 3, ruling X-28 (replaces round 2's status-code-based
    // `renderRanNothing`, which proxied the backend's restore-vs-fail
    // decision by pattern-matching HTTP status + body text and got it wrong
    // in both directions — see task-1.20-rereview-1.md). Status codes don't
    // reliably say whether the backend touched anything: a 409/500/503 can
    // either run nothing (restore) or push a fresh WS failure first (don't
    // restore over it), and that split doesn't line up with status alone.
    //
    // What DOES tell the two apart: whether the store still holds exactly
    // the optimistic marker THIS call just set. `markClipRenderStarting`
    // always mints a brand-new object, and so does every real WS/poll update
    // that supersedes it (`applyRenderProgressEvent` never returns `prev`;
    // a no-op `reconcileSeed` returns the unchanged PRIOR reference rather
    // than a new one, so it correctly never looks like "something new"
    // either). Comparing this exact reference against the store's current
    // one is therefore an exact "has anything touched this clip since I set
    // it" check — not an approximation.
    //
    // A per-request sequence-token field on `ClipRenderState` would need the
    // identical invariant (every real transition bumps it, every no-op
    // doesn't) for no extra safety, while forcing every consumer and test of
    // that widely-shared, module-level type to carry a field that means
    // nothing to them. Identity is the simpler, equally robust, less
    // invasive choice — so that's what this uses.
    let optimisticMarker = getClipRenderState(clipId);
    const restoreIfUntouched = () => {
      if (getClipRenderState(clipId) === optimisticMarker) {
        restoreClipRenderState(clipId, priorRenderState);
      }
    };
    try {
      await blurActiveElement();
      await waitForPendingSaves();
      let started: RenderStarted;
      try {
        started = await API.renderClip(clipId);
      } catch (error: any) {
        const estimate = confirmRequiredEstimate(error);
        if (estimate === null) throw error;
        // CT-10: the confirm prompt is exactly "≈ N credits — confirm render".
        if (!window.confirm(`≈ ${estimate} credits — confirm render`)) {
          restoreIfUntouched();
          toast({ text: 'Render not started', level: 'info' });
          return;
        }
        markClipRenderStarting(clipId);
        optimisticMarker = getClipRenderState(clipId);
        started = await API.renderClip(clipId, estimate);
      }
      if (typeof started?.estimate_credits === 'number') markClipRenderCancellable(clipId);
      toast({ text: renderStartedText(started), level: 'success' });
      onRefresh();
    } catch (error: any) {
      // Never blank to idle on a POST rejection, and always show the body's
      // error text regardless of the restore decision above.
      restoreIfUntouched();
      toast({ text: `Render failed: ${renderErrorText(error)}`, level: 'error' });
    } finally {
      setIsRendering(false);
    }
  };

  const handleCancelRender = async () => {
    setIsCancellingRender(true);
    try {
      await API.cancelClipRender(clipId);
      toast({ text: 'Cancelling the render…', level: 'info' });
      // Minor a: left true on success — the effect above clears it once the
      // render's live state actually stops.
    } catch (error: any) {
      setIsCancellingRender(false);
      toast({ text: `Cancel failed: ${renderErrorText(error)}`, level: 'error' });
    }
  };

  // The send is BACKGROUNDED: this await returns as soon as the job exists, not
  // when the video lands. Success and failure both arrive later on the
  // "TelegramSend" WS event, which is also what clears the button. Awaiting the
  // whole send here is exactly what used to tie a 70s transcode to the tab.
  const handleSendToTelegram = async () => {
    if (!activeUser) return;
    const videoUrl = fileUrls.find((url) => /\.(mp4|webm|mov)$/i.test(url));
    if (!videoUrl) return;
    setIsStartingTelegram(true);
    try {
      const job = await API.sendToTelegram(activeUser.id, videoUrl, clipCaption, clipId);
      markTelegramSendStarted(clipId, job.job_id);
      toast({ text: 'Sending to Telegram in the background', level: 'success' });
    } catch (error: any) {
      const msg = error?.response?.data?.error || error.message || 'Failed to send';
      toast({ text: `Telegram: ${msg}`, level: 'error' });
    } finally {
      setIsStartingTelegram(false);
    }
  };

  const handleSendCarouselToTelegram = async () => {
    if (!activeUser) return;
    setIsSendingCarousel(true);
    try {
      await API.sendCarouselToTelegram(activeUser.id, orderedImagePaths, clipCaption);
      toast({ text: 'Carousel sent to Telegram', level: 'success' });
    } catch (error: any) {
      const msg = error?.response?.data?.error || error.message || 'Failed to send';
      toast({ text: `Telegram: ${msg}`, level: 'error' });
    } finally {
      setIsSendingCarousel(false);
    }
  };

  return {
    isRendering,
    isSendingTelegram: isStartingTelegram || isTelegramSendActive(telegramSend.status),
    telegramSendLabelText: telegramSendLabel(telegramSend.status),
    isSendingCarousel,
    isCancellingRender,
    handleDelete,
    handleRender,
    handleCancelRender,
    handleSendToTelegram,
    handleSendCarouselToTelegram,
  };
}
